import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
    CheckCircle2,
    XCircle,
    Clock,
    Briefcase,
    Filter,
    Search,
    Building,
    ChevronDown,
    ChevronRight,
    ChevronLeft,
    Wind,
    Calendar,
    Info
} from 'lucide-react';
import { getWeekStart, getWeekRangeDisplay, addDays, formatDate, getDayDisplay } from './util';
import type { RenderProjectTableData, RenderUserTableData } from '../worktime';
import clsx from 'clsx';
import RejectModal from './RejectModal';
import EstimateWorktimeModal from "../daily/estimateWorktime";
import { useNavigate } from "react-router-dom"


interface AuditTableProps {
    data: RenderProjectTableData[];
    period: string;
    isAll: boolean;
    isDefaultExpand: boolean;
    currentDate: string;
    type: string;
    startDate: string;
    endDate: string;
    onRejectTimeworks: (data: Request.RejectTimeworks) => void;
    loading?: boolean;
}
const getUserStatusLabel = (status: number) => {
    if (status === 0) {
        return '待审批';
    } else if (status === 1) {
        return '已审批';
    } else if (status === 2) {
        return '有更新';
    }
    return '未知状态';
}
export default function AuditTable(props: AuditTableProps) {
    const navigate = useNavigate()
    const { data: projectsData, period } = props;

    // Expand/Collapse State for all tables
    const [isAllExpanded, setIsAllExpanded] = useState(props.isDefaultExpand);

    // Reject Modal State
    const [rejectModal, setRejectModal] = useState<{ isOpen: boolean; user: RenderUserTableData | null; reason: string, projectName: string }>({
        isOpen: false,
        user: null,
        projectName: '',
        reason: ''
    });

    // Estimate Worktime Modal State
    const [estimateModal, setEstimateModal] = useState<{ isOpen: boolean; user: RenderUserTableData | null }>({
        isOpen: false,
        user: null,
    });

    // Actions
    const handleApprove = (entryId: string) => {
        console.log('Approve:', entryId);
    };
    const goEdit = (userId: number) => {
        if (!userId) return
        navigate(`/worktime/users/${userId}/currentDate/${props.currentDate}?pageType=${props.type}`)
    }

    const openRejectModal = (user: RenderUserTableData, projectName: string) => {
        setRejectModal({ isOpen: true, user, projectName, reason: '' });
    };

    const openEstimateModal = (user: RenderUserTableData) => {
        setEstimateModal({ isOpen: true, user });
    };

    const closeEstimateModal = () => {
        setEstimateModal({ isOpen: false, user: null });
    };

    const confirmReject = (reason: string) => {
        if (!rejectModal.user || props.isAll) return;
        props.onRejectTimeworks(
            {
                userID: rejectModal.user.id,
                rejectReason: reason,
                date: props.currentDate,
                type: props.type,
            }
        )
        setRejectModal({ isOpen: false, user: null, projectName: '', reason: '' });
    };
    const closeRejectModal = () => {
        setRejectModal({ isOpen: false, user: null, projectName: '', reason: '' });
    };

    return (
        <div>
                {/* Expand/Collapse Control Button */}
                <div className="mb-4 flex justify-end">
                    <button
                        onClick={() => setIsAllExpanded(!isAllExpanded)}
                        className="inline-flex items-center px-3 py-1.5 border border-slate-200 shadow-sm text-xs rounded-lg text-slate-600 bg-white hover:bg-slate-50 transition-colors"
                    >
                        {isAllExpanded ? (
                            <><ChevronDown className="w-4 h-4 mr-1.5" />收起全部</>
                        ) : (
                            <><ChevronRight className="w-4 h-4 mr-1.5" />展开全部</>
                        )}
                    </button>
                </div>

                {/* Timesheet Data View */}
                <div className="space-y-8">
                    {projectsData.length === 0 ? (
                        <div className="text-center py-12 bg-white border border-slate-200 rounded-xl shadow-sm">
                            <div className="bg-slate-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                                <CheckCircle2 className="w-6 h-6 text-slate-300" />
                            </div>
                            <p className="text-xs text-slate-500">暂无需要审批的工时记录</p>
                            {/* <p className="text-xs text-slate-500 mt-1">当前筛选条件下没有待审批的数据</p> */}
                        </div>
                    ) : (
                        projectsData.map(project => {
                            const projectTotalHours = project.totalHours

                            return (
                                <div key={project.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                    {/* Project Header */}
                                    <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                                        <div className="flex items-center">
                                            <Building className="w-4 h-4 text-indigo-600 mr-2" />
                                            <h2 className="text-sm font-semibold text-slate-800">{project.name}</h2>
                                        </div>
                                        <div className="text-xs font-semibold text-slate-600 bg-white border border-slate-200 px-2.5 py-1 rounded-md shadow-sm">
                                            项目总工时: <span className="text-indigo-600 font-bold ml-1">{projectTotalHours} h</span>
                                        </div>
                                    </div>

                                    {/* Disciplines within Project */}
                                    <div className="divide-y divide-slate-100">
                                        {project.majors.map(major => {
                                            const disciplineTotalHours = major.totalHours

                                            return (
                                                <div key={major.name} className="p-4">
                                                    <div className={clsx("flex items-center justify-between", isAllExpanded ? "mb-3" : "")}>
                                                        <div className="flex items-center">
                                                            <div className="bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center">
                                                                {major.name}专业
                                                            </div>
                                                            <span className="ml-3 text-sm text-slate-500 font-medium">
                                                                共 {major.users.length} 名员工
                                                            </span>
                                                        </div>
                                                        <div className="text-xs font-medium text-slate-600 bg-slate-50 px-2.5 py-1 rounded-md border border-slate-100">
                                                            专业总工时: <span className="text-slate-800 font-bold ml-1">{disciplineTotalHours} h</span>
                                                        </div>
                                                    </div>

                                                    {/* Entries Table/List - Collapsible */}
                                                    <AnimatePresence>
                                                        {isAllExpanded && (
                                                            <motion.div
                                                                initial={{ opacity: 0, height: 0 }}
                                                                animate={{ opacity: 1, height: 'auto' }}
                                                                exit={{ opacity: 0, height: 0 }}
                                                                className="border border-slate-200 rounded-lg overflow-hidden"
                                                            >
                                                                <table className="w-full text-left text-sm">
                                                                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-medium whitespace-nowrap">
                                                                        <tr>
                                                                            <th className="px-3 py-2 w-48">员工</th>
                                                                            <th className="px-3 py-2 w-28">时段</th>
                                                                            <th className="px-3 py-2 w-16">设计</th>
                                                                            <th className="px-3 py-2 w-16">沟通</th>
                                                                            <th className="px-3 py-2 w-16">差旅</th>
                                                                            <th className="px-3 py-2 w-20 font-bold text-slate-800">总计</th>
                                                                            <th className="px-3 py-2 w-24">状态</th>
                                                                            {!props.isAll && !props.loading && (
                                                                                <th className="px-3 py-2 w-24 text-right">操作</th>
                                                                            )}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-slate-100 bg-white">
                                                                        {major.users.map(user => (
                                                                            <tr key={user.id} className="hover:bg-slate-50 transition-colors group">
                                                                                <td className="px-3 py-2.5 align-top">
                                                                                    <div className="flex items-center">
                                                                                        <div>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => openEstimateModal(user)}
                                                                                                title="查看预估工时"
                                                                                                className="font-medium text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors text-left"
                                                                                            >
                                                                                                {user.name}
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-3 py-2.5 align-top">
                                                                                    <span className="inline-flex items-center text-slate-600 px-1 py-0.5 rounded text-[11px]">
                                                                                        {props.period}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-3 py-2.5 align-top">
                                                                                    {user.designHours > 0 ? <span className="text-blue-600 font-medium text-xs">{user.designHours}h</span> : <span className="text-slate-300 text-xs">-</span>}
                                                                                </td>
                                                                                <td className="px-3 py-2.5 align-top">
                                                                                    {user.communicationHours > 0 ? <span className="text-purple-600 font-medium text-xs">{user.communicationHours}h</span> : <span className="text-slate-300 text-xs">-</span>}
                                                                                </td>
                                                                                <td className="px-3 py-2.5 align-top">
                                                                                    {user.travelHours > 0 ? <span className="text-orange-600 font-medium text-xs">{user.travelHours}h</span> : <span className="text-slate-300 text-xs">-</span>}
                                                                                </td>
                                                                                <td className="px-3 py-2.5 align-top">
                                                                                    <span className="text-slate-800 font-bold bg-slate-100 px-2 py-0.5 rounded text-xs">{user.totalHours}h</span>
                                                                                </td>
                                                                                <td className="px-3 py-2.5 align-top">
                                                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium w-max ${user.status === 0 ? 'bg-amber-100 text-amber-800' :
                                                                                            user.status === 1 ? 'bg-emerald-100 text-emerald-800' :
                                                                                                user.status === 2 ? 'bg-blue-100 text-blue-800' :
                                                                                                    'bg-red-100 text-red-800'
                                                                                        }`}>
                                                                                        {user.status === 0 && <Clock className="w-3 h-3 mr-1" />}
                                                                                        {user.status === 1 && <CheckCircle2 className="w-3 h-3 mr-1" />}
                                                                                        {user.status === 2 && <XCircle className="w-3 h-3 mr-1" />}
                                                                                        {getUserStatusLabel(user.status)}
                                                                                    </span>
                                                                                </td>
                                                                                {!props.isAll && !props.loading && (
                                                                                    <td className="px-3 py-2.5 align-top text-right">
                                                                                        {(user.status === 0 || user.status === 2) ? (
                                                                                            <div className="flex justify-end gap-2">
                                                                                                <button
                                                                                                    onClick={() => openRejectModal(user, project.name)}
                                                                                                    className="inline-flex items-center px-2 py-1 border border-red-300 shadow-sm text-[11px] rounded text-red-600 bg-white hover:bg-red-200"
                                                                                                >
                                                                                                    打回
                                                                                                </button>
                                                                                                <button
                                                                                                    onClick={() => goEdit(user.id)}
                                                                                                    className="inline-flex items-center px-2 py-1 border border-transparent shadow-sm text-[11px] rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                                                >
                                                                                                    修改
                                                                                                </button>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <span className="text-[11px] text-slate-400">已审批</span>
                                                                                        )}
                                                                                    </td>
                                                                                )}
                                                                                
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })
                    )}
                </div>
            {/* Reject Modal */}
            <RejectModal
                isOpen={rejectModal.isOpen}
                onClose={closeRejectModal}
                onConfirm={confirmReject}
                userName={rejectModal.user?.name || ""}
                period={period}
            />

            {/* Estimate Worktime Modal */}
            <EstimateWorktimeModal
                open={estimateModal.isOpen}
                user={estimateModal.user}
                type={props.type}
                startDate={props.startDate}
                endDate={props.endDate}
                onClose={closeEstimateModal}
            />
        </div>
    );
}
